import { useState, useMemo } from 'react'
import { Copy, Check } from 'lucide-react'
import type { CodeSnippet } from '@code-arcade/engine'

// ── Syntax highlighting ───────────────────────────────────────────────────────

type TokenType = 'keyword' | 'string' | 'comment' | 'number' | 'classname' | 'plain'

const TOKEN_COLORS: Record<TokenType, string> = {
  keyword:   '#c792ea',
  string:    '#c3e88d',
  comment:   '#546e7a',
  number:    '#f78c6c',
  classname: '#ffcb6b',
  plain:     '#cdd6f4',
}

const KEYWORDS: Record<string, string[]> = {
  javascript: [
    'function','const','let','var','return','if','else','for','while','of','in','new',
    'class','extends','import','export','default','from','true','false','null','undefined',
    'typeof','instanceof','async','await','try','catch','throw','break','continue','this',
  ],
  typescript: [
    'function','const','let','var','return','if','else','for','while','of','in','new',
    'class','extends','implements','import','export','default','from','true','false','null',
    'undefined','typeof','instanceof','async','await','try','catch','throw','break','continue',
    'this','interface','type','number','string','boolean','void','any','never','readonly','as','enum',
  ],
  python: [
    'def','return','if','else','elif','for','while','in','not','and','or','is','True','False',
    'None','import','from','class','pass','break','continue','try','except','raise','with',
    'as','lambda','yield','global','nonlocal','del','assert',
  ],
  java: [
    'public','private','protected','static','void','int','double','float','boolean','char',
    'long','byte','short','class','interface','extends','implements','return','if','else',
    'for','while','new','import','package','null','true','false','final','this','super',
    'try','catch','throw','throws','break','continue',
  ],
  csharp: [
    'public','private','protected','static','void','int','double','float','bool','string',
    'char','long','byte','short','class','interface','return','if','else','for','while',
    'new','using','namespace','null','true','false','var','readonly','const','this','base',
    'try','catch','throw','async','await','break','continue',
  ],
  pseudocode: [
    'procedure','function','if','then','else','endif','for','from','to','do','while',
    'repeat','until','return','swap','and','or','not','true','false','length',
  ],
}

function tokenize(
  code: string,
  lang: string,
): Array<Array<{ type: TokenType; text: string }>> {
  const kw = new Set(KEYWORDS[lang] ?? [])
  const usePythonComment = lang === 'python'

  return code.split('\n').map((line) => {
    const tokens: Array<{ type: TokenType; text: string }> = []
    const re = usePythonComment
      ? /(#.*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)|([A-Za-z_]\w*)/g
      : /(\/\/.*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)|([A-Za-z_]\w*)/g

    let last = 0
    let m: RegExpExecArray | null
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) tokens.push({ type: 'plain', text: line.slice(last, m.index) })
      if (m[1] !== undefined)      tokens.push({ type: 'comment', text: m[1] })
      else if (m[2] !== undefined) tokens.push({ type: 'string', text: m[2] })
      else if (m[3] !== undefined) tokens.push({ type: 'number', text: m[3] })
      else if (m[4] !== undefined) {
        const w = m[4]
        const type: TokenType = kw.has(w)
          ? 'keyword'
          : (lang === 'java' || lang === 'csharp') && /^[A-Z]/.test(w)
          ? 'classname'
          : 'plain'
        tokens.push({ type, text: w })
      }
      last = re.lastIndex
    }
    if (last < line.length) tokens.push({ type: 'plain', text: line.slice(last) })
    return tokens
  })
}

// ── Component ─────────────────────────────────────────────────────────────────

const PSEUDO_TAB = { language: 'pseudocode', label: 'Pseudocode' }

interface CodePanelProps {
  pseudocode?: string
  snippets?: CodeSnippet[]
}

export function CodePanel({ pseudocode, snippets = [] }: CodePanelProps) {
  const tabs = [
    ...(pseudocode ? [PSEUDO_TAB] : []),
    ...snippets,
  ]

  const [active, setActive] = useState(tabs[0]?.language ?? '')
  const [copied, setCopied] = useState(false)

  const activeCode =
    active === 'pseudocode'
      ? pseudocode ?? ''
      : snippets.find((s) => s.language === active)?.code ?? ''

  const tokenizedLines = useMemo(() => tokenize(activeCode, active), [activeCode, active])

  if (tabs.length === 0) return null

  const handleCopy = () => {
    navigator.clipboard.writeText(activeCode).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    })
  }

  return (
    <div className="rounded-lg border border-arcade-border overflow-hidden">
      {/* Tab bar */}
      <div className="flex overflow-x-auto bg-arcade-surface border-b border-arcade-border scrollbar-none">
        {tabs.map((tab) => (
          <button
            key={tab.language}
            onClick={() => setActive(tab.language)}
            className={`flex-shrink-0 px-3 py-2 text-xs font-mono transition-colors ${
              active === tab.language
                ? 'text-arcade-neon border-b-2 border-arcade-neon bg-arcade-bg'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Code block */}
      <div className="relative bg-arcade-bg">
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-1.5 rounded text-gray-600 hover:text-gray-300 transition-colors z-10"
          aria-label="Copy code"
        >
          {copied ? <Check size={14} className="text-arcade-neon" /> : <Copy size={14} />}
        </button>
        <pre className="overflow-x-auto p-4 pr-10 text-xs font-mono leading-relaxed whitespace-pre">
          {tokenizedLines.map((lineTokens, i) => (
            <span key={i}>
              {lineTokens.map((tok, j) => (
                <span key={j} style={{ color: TOKEN_COLORS[tok.type] }}>
                  {tok.text}
                </span>
              ))}
              {'\n'}
            </span>
          ))}
        </pre>
      </div>
    </div>
  )
}
